import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

import { useAssignmentsGet } from "@/assignments/useAssignmentsGet";
import useAssignmentViewRecordCreate from "@/assignments/useAssignmentViewRecordCreate";
import useLazyAssignmentGet from "@/assignments/useLazyAssignmentGet";
import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import { getLatestAssignment } from "@/assignments/utils/getLatestAssignment";
import { recordStudentAssignmentView } from "@/assignments/utils/recordStudentAssignmentView";
import useContentMetadataGet from "@/content/useContentMetadataGet";
import { type UseRestQueryResponse } from "@/remoteState/useRestQuery";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import { CONFIG_KEYS } from "@/view/AdditionalAssignmentSettings/AdditionalAssignmentSettings.constants";
import contentTypes from "@/view/constants/contentTypes";
import { IFRAME_CONTENT_TYPES } from "@/view/constants/contentTypes/contentTypes.constants";
import ContentLayout from "@/view/ContentLayout";
import ContentSettingsAdvisory from "@/view/ContentSettingsAdvisory";
import RelatedContent from "@/view/RelatedContent/RelatedContent";
import getAssignmentSetting from "@/view/utils/getAssignmentSetting";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import ViewPageFooter from "@/view/ViewPageFooter";
import GeniusVideo from "./GeniusVideo/GeniusVideo";
import {
  contentRootCss,
  iframeCss,
  mainSectionWrapperCss,
} from "./UnlevelledContent.style";
import UnlevelledContentActivityPanel from "./UnlevelledContentActivityPanel/UnlevelledContentActivityPanel";
import UnlevelledContentAssignActionsContainer from "./UnlevelledContentAssignActionsContainer";
import UnlevelledContentEditorContent from "./UnlevelledContentEditorContent";
import UnlevelledContentHeader from "./UnlevelledContentHeader";
import { formatGeniusContentDocument } from "./utils";
import useGeniusEditor from "./utils/useGeniusEditor/useGeniusEditor";

import type { BannerMode } from "@/types/BannerMode.interface";
import type {
  Assignment,
  AssignmentConfig,
} from "@/types/models/Assignment.interface";
import type { GenerationGeniusAPIResponse } from "@/types/models/GenerationGenius.interface";
import type {
  GenerationGeniusContent,
  IUnlevelledContent,
} from "@/types/models/UnlevelledContent.interface";
import type { UserProperties } from "@/types/models/UserProperties.interface";
import type { Metadata } from "@/view/ContentHead";

export interface UnlevelledContentProps {
  bannerMode: BannerMode;
  content: IUnlevelledContent;
}

const UnlevelledContent = ({ bannerMode, content }: UnlevelledContentProps) => {
  const contentData = content.data;
  const [duration, setDuration] = useState<string>("");
  const [showVideoOnly, setShowVideoOnly] = useState(true);

  const isGenerationGenius =
    content?.content_type === contentTypes.GENERATION_GENIUS_VIDEO;
  const isIFrameContent = IFRAME_CONTENT_TYPES.includes(content?.content_type);

  // Router
  const router = useRouter();
  const { query } = router;
  const { contentId, selectedAssignmentId } = query;

  const userProperties = useGetUserProperties() as UserProperties;
  const { isStudent, isTeacher } = userProperties;

  // Fetching assignments data
  const [fetchAssignment, assignmentRequest] = useLazyAssignmentGet();
  const { data: selectedAssignment, loading: isSelectedAssignmentLoading } =
    assignmentRequest;
  const { assignModal, dispatch, updatedAssignment } =
    useAssignmentReducerContext();
  const [postAssignmentViewRecord] = useAssignmentViewRecordCreate();

  // Fetching content metadata
  const {
    data: contentMetadata,
    error: contentMetadataRequestError,
    loading: contentMetadataLoading,
  }: UseRestQueryResponse<Metadata[]> = useContentMetadataGet(
    `${contentId}` || null,
  );

  useEffect(() => {
    // Fetch the current assignment if the selectedAssignmentId query param exists.
    fetchAssignment({
      id: selectedAssignmentId,
      queryParams: {
        content_member_id: contentId as string,
      },
      skip: !getIsSavedAssignment(selectedAssignmentId),
    });
  }, []);

  useEffect(() => {
    const shouldShowVideoOnly = getAssignmentSetting(
      CONFIG_KEYS.show_generation_genius_video_only as keyof AssignmentConfig,
      updatedAssignment,
    );
    setShowVideoOnly(isStudent && shouldShowVideoOnly);
  }, [isStudent, updatedAssignment]);

  // This fetches assignment data specific to the article content.
  const { data: userAssignments, loading: assignmentsLoading } =
    useAssignmentsGet({
      include_drafts: isTeacher,
      content_member_id: contentId as string,
    });
  const publishedUserAssignments = (
    (userAssignments as Assignment[]) || []
  ).filter((assignment) => !assignment.is_draft);

  const latestAssignment = getLatestAssignment(publishedUserAssignments);

  const currentAssignment = isStudent
    ? selectedAssignment || latestAssignment
    : selectedAssignment;

  useEffect(() => {
    recordStudentAssignmentView({
      isStudent,
      latestAssignment,
      publishedUserAssignments,
      postAssignmentViewRecord,
    });
  }, [JSON.stringify(publishedUserAssignments)]);

  const formattedContent = useMemo(
    () =>
      isGenerationGenius
        ? formatGeniusContentDocument(
            contentData as GenerationGeniusAPIResponse,
          )
        : null,
    [JSON.stringify(contentData)],
  );

  const editor = useGeniusEditor({ content: formattedContent });

  if (!editor) return null;

  return (
    <div css={contentRootCss}>
      <ContentSettingsAdvisory
        isStudent={isStudent}
        visibilityMarkers={content.visibility_markers}
      />
      <ContentLayout inline={false}>
        <ContentLayout.StickyBarContainer overrideToTop>
          <UnlevelledContentAssignActionsContainer
            assignmentRequest={assignmentRequest}
            assignModal={assignModal}
            bannerMode={bannerMode}
            content={content}
            currentAssignment={currentAssignment as Assignment}
            dispatch={dispatch}
            fetchAssignment={fetchAssignment}
            isSelectedAssignmentLoading={isSelectedAssignmentLoading}
            latestAssignment={latestAssignment}
            updatedAssignment={updatedAssignment as Assignment}
            userProperties={userProperties}
            router={router}
          />
        </ContentLayout.StickyBarContainer>
        <ContentLayout.Header>
          {isGenerationGenius && (
            <GeniusVideo
              isVideoOnly={showVideoOnly}
              content={content as GenerationGeniusContent}
              setDuration={setDuration}
            />
          )}
          {isIFrameContent && (
            <iframe
              css={iframeCss}
              src={content.data.url}
              title={content.data.title}
              allowFullScreen
            />
          )}
        </ContentLayout.Header>
        {!showVideoOnly && (
          <ContentLayout.Panel contentIsVideo>
            <UnlevelledContentActivityPanel
              assignments={publishedUserAssignments}
              assignmentsLoading={assignmentsLoading}
              content={content}
            />
          </ContentLayout.Panel>
        )}
        <ContentLayout.MainSection>
          <div css={mainSectionWrapperCss(showVideoOnly)}>
            <UnlevelledContentHeader
              content={content}
              duration={duration}
              bannerMode={bannerMode}
            />
            <UnlevelledContentEditorContent editor={editor} />
          </div>
        </ContentLayout.MainSection>
        <ContentLayout.PageBottom>
          {!contentMetadataRequestError && !contentMetadataLoading && (
            <RelatedContent
              content={content}
              contentMetadata={contentMetadata || []}
            />
          )}
          <ViewPageFooter />
        </ContentLayout.PageBottom>
      </ContentLayout>
    </div>
  );
};

export default UnlevelledContent;
